<template>
  <!-- Backdrop -->
  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0">
    <div v-if="open" class="fixed inset-0 bg-black/30 dark:bg-black/50 z-40" @click="$emit('close')" />
  </Transition>

  <!-- Drawer -->
  <Transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full">
    <div
      v-if="open"
      class="fixed right-0 top-0 h-full w-full sm:w-96 bg-white dark:bg-[#1C2128] shadow-2xl z-50 flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
        <div>
          <h2 class="text-base font-bold text-gray-900 dark:text-white">Notification Sound</h2>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Choose a sound and volume for new notifications</p>
        </div>
        <button
          @click="$emit('close')"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Scrollable content -->
      <div class="flex-1 overflow-y-auto px-5 py-5 space-y-6">

        <!-- Sound picker -->
        <div>
          <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Sound</p>
          <div class="space-y-2">
            <button
              v-for="sound in SOUNDS"
              :key="sound.id"
              @click="selectSound(sound.id)"
              class="w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left"
              :class="localSound === sound.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                : 'border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128] hover:border-gray-300 dark:hover:border-gray-600'"
            >
              <!-- Selection indicator -->
              <div
                class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors"
                :class="localSound === sound.id
                  ? 'border-blue-600 bg-blue-600'
                  : 'border-gray-300 dark:border-gray-600'"
              >
                <div v-if="localSound === sound.id" class="w-1.5 h-1.5 rounded-full bg-white"></div>
              </div>

              <!-- Label -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{{ sound.label }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ sound.description }}</p>
              </div>

              <!-- Preview button (not for 'none') -->
              <button
                v-if="sound.id !== 'none'"
                @click.stop="preview(sound.id)"
                class="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors"
                :class="previewingId === sound.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400'"
                title="Preview"
              >
                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            </button>
          </div>
        </div>

        <!-- Volume slider -->
        <div>
          <div class="flex items-center justify-between mb-3">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Volume</p>
            <span class="text-sm font-bold text-blue-600 dark:text-blue-400 tabular-nums">{{ localVolume }}%</span>
          </div>

          <div class="flex items-center gap-3">
            <!-- Quiet icon -->
            <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
            </svg>

            <input
              type="range"
              min="0"
              max="100"
              step="5"
              v-model.number="localVolume"
              class="vol-range flex-1"
              :style="sliderStyle"
            />

            <!-- Loud icon -->
            <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
            </svg>
          </div>
        </div>

      </div>

      <!-- Footer actions -->
      <div class="flex-shrink-0 px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3">
        <button
          @click="$emit('close')"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
        >Cancel</button>
        <button
          @click="save"
          :disabled="saving"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useGlobalStore } from 'stores/global.js'
import { useRequest } from 'composables/useRequest.js'
import { SOUNDS, playSound, unlockAudio } from 'controllers/vue_components/notifications/notificationSounds.js'

const props = defineProps({ open: Boolean })
const emit  = defineEmits(['close', 'saved'])

const globalStore  = useGlobalStore()
const localSound   = ref(globalStore.user.notification_sound  ?? 'ding')
const localVolume  = ref(globalStore.user.notification_volume ?? 80)
const saving       = ref(false)
const previewingId = ref(null)

// Re-sync local state whenever drawer opens
watch(() => props.open, (val) => {
  if (val) {
    localSound.value  = globalStore.user.notification_sound  ?? 'ding'
    localVolume.value = globalStore.user.notification_volume ?? 80
  }
})

function selectSound(id) {
  localSound.value = id
}

function preview(soundId) {
  unlockAudio() // ensure context is running before previewing
  previewingId.value = soundId
  playSound(soundId, localVolume.value)
  setTimeout(() => { previewingId.value = null }, 1500)
}

async function save() {
  saving.value = true
  const { axiosRequest } = useRequest()
  const resp = await axiosRequest('PUT', '/api/v1/user', {
    user: { notification_sound: localSound.value, notification_volume: localVolume.value }
  }, false)
  saving.value = false
  if (!resp.js_error && resp.response?.success) {
    globalStore.user.notification_sound  = localSound.value
    globalStore.user.notification_volume = localVolume.value
    emit('saved')
    emit('close')
  }
}

const sliderStyle = computed(() => {
  const pct   = localVolume.value + '%'
  const isDark = document.documentElement.classList.contains('dark')
  const empty  = isDark ? '#374151' : '#e5e7eb'
  return { background: `linear-gradient(to right, #3b82f6 ${pct}, ${empty} ${pct})` }
})
</script>

<style scoped>
.vol-range {
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 9999px;
  outline: none;
  cursor: pointer;
}

.vol-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  border: 2.5px solid #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.vol-range::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 3px 12px rgba(59, 130, 246, 0.55);
}

.vol-range::-webkit-slider-thumb:active {
  transform: scale(1.1);
}

.vol-range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  border: 2.5px solid #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  cursor: pointer;
}

.vol-range::-moz-range-track {
  height: 6px;
  border-radius: 9999px;
}
</style>
